import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BusinessThemeProvider, useBusinessTheme } from './contexts/BusinessThemeContext';
import { createAppTheme } from './theme';
import Layout from './components/Layout';
import { Login } from './pages/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
import Dashboard from './pages/Dashboard';
import MyAccount from './pages/MyAccount';
import BusinessSettings from './pages/BusinessSettings';
import CalendarPage from './pages/Calendar';
import { Box, CircularProgress, Typography } from '@mui/material';
//import PaymentDetails from './pages/PaymentDetails';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { DEFAULT_THEME, type ThemeColors } from './constants/themeDefaults';
import { usePaymentIntent } from './hooks/usePaymentIntent';
import PaymentSuccess from './pages/PaymentSuccess';
//import { Elements } from '@stripe/react-stripe-js';
//import { stripePromise } from './lib/stripe';
import { HelmetProvider } from 'react-helmet-async';

import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import TermsOfServicePage from './pages/public/TermsOfServicePage';
import { CreditsPage } from './pages/public/CreditsPage';

// Add these imports at the top of the file
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import BusinessSetup from './pages/BusinessSetup';
import PendingSetupPage from './pages/PendingSetupPage';
import AppointmentTypes from './pages/AppointmentTypes';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { QueryProvider } from './providers/QueryProvider';
import Roles from './pages/Roles';
import Users from './pages/Users';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import { FormEditorPage } from './pages/FormEditorPage';
import AdminDiagnosticsPage from './pages/AdminDiagnosticsPage';
import AdminUserDataPage from './pages/AdminUserDataPage';

// Loading screen component - Updated to use only DEFAULT_THEME
const LoadingScreen = () => {
    // const { theme: businessTheme } = useBusinessTheme(); // Removed useBusinessTheme
    const [loadingMessage, setLoadingMessage] = useState('Initializing application...');
    
    // Theme values using only DEFAULT_THEME
    const navigationColor = DEFAULT_THEME.navigationColor;
    const backgroundColor = DEFAULT_THEME.backgroundColor;
    const secondaryColor = DEFAULT_THEME.secondaryColor;
    const textColor = DEFAULT_THEME.textColor;
    const accentColor = DEFAULT_THEME.accentColor; // Use default accent for progress

    useEffect(() => {
        const messages = [
            'Initializing application...',
            'Loading your preferences...',
            'Preparing your calendar...',
            'Almost there...'
        ];
        let currentIndex = 0;

        const interval = setInterval(() => {
            currentIndex = (currentIndex + 1) % messages.length;
            setLoadingMessage(messages[currentIndex]);
        }, 2000);

        return () => clearInterval(interval);
    }, []);

    return (
        <Box 
            sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                width: '100vw',
                position: 'fixed',
                top: 0,
                left: 0,
                backgroundColor: backgroundColor,
                gap: 5
            }}
        >
            {/* MindShare Logo and Name */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <CalendarMonthIcon sx={{ color: navigationColor, mr: 1, fontSize: '3rem' }} />
                <Typography variant="h3" fontWeight="700" color={textColor}>
                    MindShare
                </Typography>
            </Box>
            
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4
                }}
            >
                <CircularProgress 
                    size={60}
                    thickness={4}
                    sx={{ 
                        color: accentColor,
                        '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round',
                        }
                    }}
                />
                <Typography 
                    variant="h6" 
                    sx={{ 
                        color: textColor,
                        textAlign: 'center',
                        fontWeight: 500,
                        opacity: 0.9,
                        animation: 'fadeInOut 2s infinite',
                        '@keyframes fadeInOut': {
                            '0%': { opacity: 0.4 },
                            '50%': { opacity: 0.9 },
                            '100%': { opacity: 0.4 }
                        }
                    }}
                >
                    {loadingMessage}
                </Typography>
            </Box>
        </Box>
    );
};

//const PaymentWrapper = () => {
//  const { priceId } = useParams();
//  console.log("PaymentWrapper mounted with priceId:", priceId);
//  
//  if (!priceId) {
//    return <Navigate to="/pricing" replace />;
//  }
  
  // Don't use Elements here - pass the priceId to PaymentDetails
//  return <PaymentDetails priceId={priceId} />;
//};

// For regular protected routes (dashboard, etc)
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, hasBusinessSetup, user, isOfflineAuth, needsReauth } = useAuth();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // If user has no business and isn't a system admin, show pending message
  if (!hasBusinessSetup && !user?.isAdmin) {
    return <Navigate to="/pending-setup" />;
  }
  
  return <>{children}</>;
};

// Specific for business setup - shows only when no business exists
const BusinessSetupRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, hasBusinessSetup, user } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // If they have a business or are a system admin, don't show setup
  if (hasBusinessSetup || user?.isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

const AppContent = () => {
    const { theme: businessTheme } = useBusinessTheme();
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation(); // Get current location

    // Determine the theme based on authentication and route
    // IMPORTANT: This must come BEFORE any early returns to comply with Rules of Hooks
    const themeToApply = useMemo(() => {
        // Define public paths (now limited to legal and auth pages)
        const publicPaths = [
            '/privacy', '/terms', '/credits', '/login',
            '/forgot-password', '/reset-password', '/verify-email'
        ];
        const isPublicRoute = publicPaths.some(path => location.pathname.startsWith(path));

        // Apply business theme only if authenticated AND on a non-public route
        const applyBusinessTheme = isAuthenticated && !isPublicRoute;

        // console.log(`Path: ${location.pathname}, IsPublic: ${isPublicRoute}, IsAuthenticated: ${isAuthenticated}, Apply Business Theme: ${applyBusinessTheme}`); // Debugging line

        return createAppTheme(applyBusinessTheme ? businessTheme : undefined);
    }, [location.pathname, isAuthenticated, businessTheme]);

    // Show loading screen while authentication state is being determined
    // This early return comes AFTER all hooks to comply with Rules of Hooks
    if (isLoading) {
        return <LoadingScreen />; // Loading screen now uses default theme
    }

    return (
        <ThemeProvider theme={themeToApply}> {/* Use the dynamically determined theme */}
            <CssBaseline />
            
            {/* Add PWA install prompt */}
            <PWAInstallPrompt />
            
            <Routes>
                {/* Redirect root to login */}
                <Route path="/" element={<Navigate to="/login" />} />
                
                {/* Legal Routes */}
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/credits" element={<CreditsPage />} />
                
                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                
                {/* Protected App Routes */}
                <Route path="/dashboard" element={
                    <ProtectedRoute>
                        <Layout>
                            <Dashboard />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/account" element={
                    <ProtectedRoute>
                        <Layout>
                            <MyAccount />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/business-settings" element={
                    <ProtectedRoute>
                        <Layout>
                            <BusinessSettings />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/calendar" element={
                    <ProtectedRoute>
                        <Layout>
                            <CalendarPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                {/* Add Routes for Roles and Users pages */}
                <Route path="/roles" element={
                    <ProtectedRoute>
                        <Layout>
                            <Roles />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/users" element={
                    <ProtectedRoute>
                        <Layout>
                            <Users />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                {/* CRM Routes */}
                <Route path="/crm/customers" element={
                    <ProtectedRoute>
                        <Layout>
                            <CustomersPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/crm/customers/:id/*" element={
                    <ProtectedRoute>
                        <Layout>
                            <CustomerDetailPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/crm/jobs" element={
                    <ProtectedRoute>
                        <Layout>
                            <JobsPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/crm/jobs/:id" element={
                    <ProtectedRoute>
                        <Layout>
                            <JobDetailPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                {/* Admin Routes */}
                <Route path="/admin/form-editor" element={
                    <ProtectedRoute>
                        <FormEditorPage />
                    </ProtectedRoute>
                } />
                <Route path="/admin/diagnostics" element={
                    <ProtectedRoute>
                        <Layout>
                            <AdminDiagnosticsPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                <Route path="/admin/user-data" element={
                    <ProtectedRoute>
                        <Layout>
                            <AdminUserDataPage />
                        </Layout>
                    </ProtectedRoute>
                } />
                
                {/* Payment Routes */}
                {/*<Route 
                    path="/payment-details/:priceId" 
                    element={<PaymentWrapper />} 
                />*/}
                
                {/* Payment Success Route */}
                <Route path="/payment-success" element={<PaymentSuccess />} />
                
                {/* Pending setup - shown to users without a business */}
                <Route path="/pending-setup" element={<PendingSetupPage />} />

                {/* Business setup - only accessible by system admins */}
                <Route
                    path="/business-setup"
                    element={
                        <BusinessSetupRoute>
                            <Layout>
                                <BusinessSetup />
                            </Layout>
                        </BusinessSetupRoute>
                    }
                />
                
                {/* Update other routes that require business setup */}
                <Route 
                    path="/appointment-types" 
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <AppointmentTypes />
                            </Layout>
                        </ProtectedRoute>
                    } 
                />
                
                {/* Payment Wrapper Route */}
                {/*<Route path="/payment/:priceId" element={<PaymentWrapper />} />*/}
                
                {/* Fallback for unknown routes */}
                <Route path="*" element={<Navigate to="/" />} /> 
            </Routes>
        </ThemeProvider>
    );
};

function App() {
    return (
        <HelmetProvider>
            <Router>
                <BusinessThemeProvider>
                    <AuthProvider>
                        <QueryProvider>
                            <OnboardingProvider>
                                <AppContent />
                            </OnboardingProvider>
                        </QueryProvider>
                    </AuthProvider>
                </BusinessThemeProvider>
            </Router>
        </HelmetProvider>
    );
}

export default App;